import { inject, Injectable } from '@angular/core';
import { Router, UrlSerializer, UrlTree } from '@angular/router';

import {
  AccountPrivilegeType,
  AdminLogDto,
  AiSearchAdjustmentType,
  AiSearchDto,
  ConditionInfo,
  FacetResponseData,
  FacetType,
  HandledAutocompleteResponse,
  PaginationInfo,
  SearchHistoryInfo,
  SearchHistoryQueries,
  SearchQueryFromHistory,
  SearchQueryInfo,
  SearchQueryRequestInfo,
  SearchRequestInfo,
  SearchResponse,
  SearchState,
  SuggestedFilterChip,
  SuggestedFiltersState,
} from '../../../../shared/models';
import { SearchData, SearchQueriesInfo, SearchUtilities } from './search.utilities';
import { BehaviorSubject, catchError, combineLatest, Observable, of, Subject, tap } from 'rxjs';
import { AiSearchEditGuardService, AiSearchStateService } from '../../../search/ai-search/services';
import { Facets, FiltersService, FiltersUtilities } from '../../../search/shared/services';
import { DefaultSearch } from '../../../../shared/enums';
import {
  AiSearchService,
  AutocompleteService,
  UtilitiesService,
} from '../../../../shared/services';
import { LocationService } from 'src/app/core/services/global';
import { ParseFiltersInfo } from '../../models/search-filter';
import { SystemPages } from '../../../../shared/enums/routerPaths.enum';
import { SearchFilter } from '../../classes';
import { filter, map, switchMap } from 'rxjs/operators';

@Injectable()
export class SearchService {
  private readonly router = inject(Router);
  private readonly serializer = inject(UrlSerializer);
  private readonly filtersService = inject(FiltersService);
  private readonly utilities = inject(UtilitiesService);
  private readonly autocompleteService = inject(AutocompleteService);
  private readonly locationService = inject(LocationService);
  private readonly aiSearchStateService = inject(AiSearchStateService);
  private readonly aiSearchService = inject(AiSearchService);
  private readonly editGuard = inject(AiSearchEditGuardService);

  private state: SearchState = SearchState.SENT;
  private requestInfo: SearchRequestInfo;
  private autocompleteResponse?: HandledAutocompleteResponse;
  private facetResponse?: FacetResponseData;
  private updateState$ = new BehaviorSubject<SearchState>(this.state);
  private updateSearchQuery$ = new Subject<string>();
  private updateLoading$ = new BehaviorSubject<ConditionInfo>({
    condition: false,
  });
  private newSearchSubject = new Subject<null>();
  private forceSearchRefreshSrc$ = new BehaviorSubject<SearchRequestInfo | null>(null);
  private searchStartedSrc$ = new BehaviorSubject<boolean>(false);

  readonly forceSearchRefresh$: Observable<SearchRequestInfo> =
    this.forceSearchRefreshSrc$.asObservable();
  readonly state$: Observable<SearchState> = this.updateState$.asObservable();
  readonly searchQuery$: Observable<string> = this.updateSearchQuery$.asObservable();
  readonly loading$: Observable<ConditionInfo> = this.updateLoading$.asObservable();
  readonly newSearch$: Observable<null> = this.newSearchSubject.asObservable();
  readonly adjustmentContext$ = this.aiSearchStateService.adjustmentContext$;
  readonly suggestedFilters$: Observable<SuggestedFiltersState | null> = combineLatest([
    this.aiSearchStateService.suggestedFilters$,
    this.searchStartedSrc$,
  ]).pipe(
    filter(([sf]) => !!sf),
    map(([sf, searchStarted]) => {
      return searchStarted ? { ...sf, visible: false } : sf;
    }),
  );

  // ******************** 1. Loading *********************

  startLoading(): void {
    this.updateLoading(true);
  }

  finishLoading(): void {
    this.updateLoading(false);
  }

  private updateLoading(condition: boolean): void {
    this.updateLoading$.next({ condition });
  }

  // ******************** 2. State *********************

  applySuggestedFilter(chip: SuggestedFilterChip): void {
    if (chip.applied) {
      return;
    }

    const { id, label, source, isAdditional } = chip;
    let value: string | number = label;

    if (source === 'experience') {
      const parsed = parseInt(chip.label, 10);

      if (!isNaN(parsed)) {
        value = parsed;
      }
    }

    this.filtersService.applyIndividualFilter(source, value, isAdditional);
    this.aiSearchStateService.markChipApplied(id);
  }

  applyAiSearchStateUpdate(state: SearchState | null): void {
    if (state) {
      this.updateState(state);
    }
  }

  onAiSearchEnabledChanged(): void {
    const isEnabled = this.aiSearchStateService.getIsEnabled();

    if (isEnabled) {
      // When AI is turned ON, searchStateUpdate$ handles it automatically
      return;
    }

    // AI turned OFF — re-evaluate purely from filters
    const info: SearchQueriesInfo = this.filtersService.composeQueriesInfo();
    const hasFilters = !!(info?.queryList?.length || info?.filterList?.length);
    this.updateFiltersValidity(hasFilters);
  }

  updateFiltersValidity(filtersValid: boolean): void {
    if (this.aiSearchStateService.getIsSearchModified()) {
      this.updateState(SearchState.NEW_DISABLED);
      return;
    }

    const hasActiveAiSearch =
      this.aiSearchStateService.hasAiSearch() && this.aiSearchStateService.getIsEnabled();

    if (filtersValid || hasActiveAiSearch) {
      this.updateState(SearchState.NEW);
    } else {
      this.updateState(SearchState.NEW_DISABLED);
    }
  }

  // ******************** 3. Search ********************

  // 3. Launch search

  search(): void {
    // Launch search (on click on Search btn or automatically for launch default search)
    const info: SearchQueriesInfo = this.filtersService.composeQueriesInfo();
    const aiSearchId = this.aiSearchStateService.getAiSearchId();
    const data: SearchData = {
      ...info,
      aiSearchId,
      aiSearchOn: aiSearchId ? this.aiSearchStateService.getIsEnabled() : undefined,
    };

    this.updateRequestInfo(data, true);
    this.updateState(SearchState.SENT);

    if (aiSearchId) {
      this.aiSearchStateService.setGlobalSearchSubmitted(true);
    }

    this.aiSearchStateService.hideSuggestedFilters();
    this.newSearchSubject.next(null);

    if (aiSearchId && this.isNavigatingToSameUrl()) {
      this.forceSearchRefreshSrc$.next(this.requestInfo);
    } else {
      this.router
        .navigate([SystemPages.SEARCH_RESULTS], { queryParams: this.requestInfo })
        .then(() => {});
    }
  }

  onNewSearch(): void {
    this.router.navigate([SystemPages.SEARCH_RESULTS]).then(() => {
      this.clearSearch();
    });
  }

  searchByQuery(query: string, newTab: boolean = false): void {
    // launch search by query (from Saved Searches, Search History, Admin Panel users logs)
    const searchRequestInfo: SearchRequestInfo = this.getSearchRequestInfoBySearchItem(query);

    if (newTab) {
      const url = this.router.serializeUrl(
        this.router.createUrlTree([SystemPages.SEARCH_RESULTS], {
          queryParams: searchRequestInfo,
        }),
      );

      window.open(url, '_blank');
    } else {
      this.filtersService.resetFilters();

      this.newSearchSubject.next(null);
      this.router
        .navigate(['/'])
        .then(() => {
          return this.router.navigate([SystemPages.SEARCH_RESULTS], {
            queryParams: searchRequestInfo,
          });
        })
        .then(() => {});
    }
  }

  updateSearchContent(data: SearchData): void {
    this.startLoading();
    this.updateRequestInfo(data, false);

    // Navigate via Router so browser history stays in sync (enables back/forward)
    this.router.navigate([], {
      queryParams: this.requestInfo,
    });
  }

  replaceQueryByPage(pageNumber: number): void {
    if (this.requestInfo && pageNumber) {
      this.requestInfo = { ...this.requestInfo, pageNumber };
      const searchQuery: string = this.getSearchQuery(this.requestInfo);

      this.locationService.replaceQuery(searchQuery);
    }
  }

  navigateToEmptySearch(): void {
    // Navigate to new (empty) search (this method resets previous search if such exists)
    this.router.navigate(['/']).then(() => {});
  }

  clearSearch(): void {
    this.resetSearchInfo();
    this.updateState(SearchState.EMPTY);
    this.filtersService.setDefaultFilters();
    this.aiSearchStateService.clear();
    this.forceSearchRefreshSrc$.next(null);
    this.searchStartedSrc$.next(false);
    this.updateSearchQuery$.next(null);
    this.finishLoading();
  }

  // 3.2 Display search

  // Clear from here
  showNewSearch(): void {
    // Display new (empty) search (instruction gets from url empty params)
    this.resetSearchInfo();
    this.updateState(SearchState.EMPTY);
    this.filtersService.setDefaultFilters();
    this.aiSearchStateService.clear();
    this.searchStartedSrc$.next(false);

    this.locationService.replaceQuery('');

    this.newSearchSubject.next(null);
    this.updateSearchQuery('');
  }

  showSentSearch(parseFiltersInfo: ParseFiltersInfo): void {
    // Display search from url
    // (instruction gets from url by existed query params, converted to SearchRequestInfo)
    this.startLoading();

    const { requestInfo } = parseFiltersInfo;

    this.setSearchInfo(parseFiltersInfo);
    this.updateState(SearchState.SENT);
    this.filtersService.resetFilters();
    this.filtersService.setFiltersByRequestInfo(parseFiltersInfo);

    if (!requestInfo.aiSearchId) {
      this.aiSearchStateService.clear();
    }

    const queriesInfo: SearchQueriesInfo = this.filtersService.composeQueriesInfo(true);
    const requestInfoForApi: SearchRequestInfo = SearchUtilities.requestInfoForApi(
      requestInfo,
      queriesInfo,
    );
    const searchQuery: string = this.getSearchQuery(requestInfoForApi);

    this.updateSearchQuery(searchQuery);
  }

  // 3.3 Search getters:

  getPageNumberFromUrl(): number | null {
    return this.requestInfo.pageNumber ? +this.requestInfo.pageNumber : null;
  }

  getPaginationInfoByResponse(searchResponse: SearchResponse): PaginationInfo {
    if (searchResponse) {
      return {
        numFound: searchResponse?.numFound,
        page: searchResponse?.page,
        pageSize: searchResponse?.pageSize,
      };
    }

    return null;
  }

  getSearchRequestInfoBySearchItem(searchQuery: string): SearchRequestInfo {
    if (searchQuery) {
      const queryQuestionMarkMatch: boolean = !!searchQuery?.match(/\?/);
      let query: string = searchQuery;

      if (!queryQuestionMarkMatch) {
        query = `?${query}`;
      }

      const urlTree: UrlTree = this.serializer.parse(query);

      return urlTree?.queryParams as SearchRequestInfo;
    }

    return null;
  }

  getSearchQueryForSavingSearch(searchName: string, searchRequestInfo): SearchQueryRequestInfo {
    if (searchName && searchRequestInfo) {
      const searchRequestInfoWithoutPageNumber: SearchRequestInfo = {
        ...searchRequestInfo,
      };

      delete searchRequestInfoWithoutPageNumber.pageNumber;

      let queryString: string = this.getSearchQuery(searchRequestInfoWithoutPageNumber);
      queryString = queryString?.replace('?', '');

      const searchNameNormalized: string = searchName?.trim();

      return {
        name: searchNameNormalized,
        query: queryString,
      };
    }

    return null;
  }

  // ***************** Shared methods *****************

  isAdjustmentAddingInProgress(): boolean {
    return this.aiSearchStateService.isAdjustmentAddingInProgress();
  }

  updateProfileAdjustment(docId: string, type: AiSearchAdjustmentType | null): Observable<null> {
    return this.editGuard.confirmEditIfNotOwner().pipe(
      filter(Boolean),
      switchMap(() => this.aiSearchStateService.updateAdjustment(type, docId)),
      tap((dto: AiSearchDto | null) => this.editGuard.handleEditResponseIfForked(dto)),
      map(() => null),
    );
  }

  resetAiSearchState(): void {
    this.aiSearchStateService.clear();
  }

  // ****************** 4. Set data inner methods ******************

  private resetSearchInfo(): void {
    this.requestInfo = {};
    this.autocompleteResponse = null;
    this.facetResponse = null;
  }

  private setSearchInfo(info: ParseFiltersInfo): void {
    this.requestInfo = { ...info.requestInfo };
    this.autocompleteResponse = info.autocompleteResponse;
    this.facetResponse = info.facetResponse;
  }

  private updateState(state: SearchState): void {
    this.state = state;

    this.updateState$.next(state);
  }

  private updateSearchQuery(searchQuery: string): void {
    if (searchQuery) {
      this.searchStartedSrc$.next(true);
    }

    this.updateSearchQuery$.next(searchQuery);
  }

  private updateRequestInfo(data: SearchData, withQueries: boolean = false): void {
    if (this.utilities.doesObjectEmpty(this.requestInfo)) {
      this.requestInfo = SearchUtilities.getSearchRequestInfo(data);
    } else {
      this.updateExistedRequestInfo(data, withQueries);
    }
  }

  private updateExistedRequestInfo(data: SearchData, withQueries: boolean): void {
    const { pageNumber, sort, order, queryList, filterList } = data;
    const aiSearchId = this.aiSearchStateService.getAiSearchId();

    if (withQueries) {
      this.requestInfo.pageNumber = DefaultSearch.PAGE_NUMBER;
    } else if (pageNumber && pageNumber !== this.requestInfo.pageNumber) {
      this.requestInfo.pageNumber = pageNumber;
    }

    if (sort && sort !== this.requestInfo.sort) {
      this.requestInfo.sort = sort;
      this.requestInfo.pageNumber = DefaultSearch.PAGE_NUMBER;
    }

    if (order && order !== this.requestInfo.order) {
      this.requestInfo.order = order;
      this.requestInfo.pageNumber = DefaultSearch.PAGE_NUMBER;
    }

    if (withQueries) {
      this.requestInfo.q = queryList?.length ? queryList : [];
      this.requestInfo.f = filterList?.length ? filterList : [];
    }

    if (aiSearchId) {
      this.requestInfo.aiSearchId = aiSearchId;
      this.requestInfo.aiSearchOn = this.aiSearchStateService.getIsEnabled();
    } else {
      delete this.requestInfo.aiSearchOn;
    }
  }

  // ****************** 5. Get search query methods *******************

  // 5.1 Public search query methods

  getSearchQuery(requestInfo: SearchRequestInfo = null): string {
    if (!requestInfo) {
      requestInfo = SearchUtilities.getSearchRequestInfo();
    }

    return this.getSearchQueryByInfo(requestInfo);
  }

  getSearchQueryForApi(): string {
    if (!this.requestInfo) {
      return '';
    }

    const infoForApi = this.getRequestInfoForApi(this.requestInfo);
    infoForApi.pageNumber = undefined;
    return this.getSearchQueryByInfo(infoForApi);
  }

  getQueryForSearchObjects(requestInfo: SearchRequestInfo): string {
    return this.getSearchQuery(requestInfo);
  }

  getCurrentSearchQuery(): string {
    return this.requestInfo ? this.getSearchQueryByInfo(this.requestInfo) : '';
  }

  private getRequestInfoForApi(requestInfo: SearchRequestInfo): SearchRequestInfo {
    const filtersForAPI: SearchFilter[] = FiltersUtilities.getParsedFilters({
      requestInfo,
    });
    const queriesInfo: SearchQueriesInfo = this.filtersService.composeQueriesInfo(
      true,
      filtersForAPI,
    );
    return SearchUtilities.requestInfoForApi(requestInfo, queriesInfo);
  }

  getSearchQueryInfo(requestInfo: SearchRequestInfo): SearchQueryInfo {
    const requestInfoForApi = this.getRequestInfoForApi(requestInfo);
    const queryForSearchObjects: string = this.getQueryForSearchObjects(requestInfoForApi);
    const queryWithFacets: string = this.getSearchQueryWithFacets(requestInfoForApi);

    return {
      queryForSearchObjects,
      queryWithFacets,
    };
  }

  getCurrentSearchQueryWithFacets(facetTypes: FacetType[]): string {
    if (!facetTypes?.length) {
      return '';
    }

    const requestInfoForApi = this.getRequestInfoForApi(this.requestInfo);

    return this.getSearchQueryWithFacets(requestInfoForApi, facetTypes);
  }

  getRequestInfoFromQuery(query: string): SearchRequestInfo {
    const tree: UrlTree = this.serializer.parse(`?${query}`);
    const queries: string[] = tree.queryParams?.q;
    const filters: string[] = tree.queryParams?.f;

    return {
      q: queries,
      f: filters,
    };
  }

  getSearchHistoryMaps(queriesInfo: SearchQueryFromHistory[]): SearchHistoryInfo[] {
    return queriesInfo.map((item: SearchQueryFromHistory) => this.getSearchHistoryMap(item));
  }

  getSearchHistoryMapByLog(log: AdminLogDto): SearchHistoryInfo {
    const queryInfo: SearchQueryFromHistory = this.getSearchQueryFromHistoryByLog(log);

    return this.getSearchHistoryMap(queryInfo);
  }

  // 5.2 Private search query methods

  private getSearchQueryWithFacets(
    requestInfo: SearchRequestInfo,
    facets: FacetType[] = null,
  ): string {
    if (!facets?.length) {
      facets = this.getFacetsFromRequestInfo(requestInfo);
    }

    if (facets.length) {
      const resultRequestInfo: SearchRequestInfo = {
        ...requestInfo,
        facet: facets,
      };

      return this.getSearchQuery(resultRequestInfo);
    }

    return null;
  }

  private getFacetsFromRequestInfo(requestInfo: SearchRequestInfo): FacetType[] {
    const reveledFiltersFacets: FacetType[] = this.filtersService.getRevealedFilterFacets();
    const facetsByRequestInfo: FacetType[] = Facets.getFacetsFromRequestInfo(requestInfo);
    const combinedFacets: FacetType[] = reveledFiltersFacets.concat(facetsByRequestInfo);

    return [...new Set(combinedFacets)];
  }

  private getSearchQueryByInfo(searchRequestInfo: SearchRequestInfo): string {
    const tree: UrlTree = this.router.createUrlTree([], {
      queryParams: searchRequestInfo,
    });
    const url: string = this.serializer.serialize(tree);
    const fullyEncodedUrl: string = SearchService.getFullyEncodedUrl(url);

    return SearchService.getQueryStringFromUrl(fullyEncodedUrl);
  }

  private getSearchHistoryMap(queryInfo: SearchQueryFromHistory): SearchHistoryInfo {
    const { query, dm } = queryInfo;
    const info: ParseFiltersInfo = this.getParseFiltersInfo(queryInfo);
    const queries: SearchHistoryQueries[] = this.filtersService.getFiltersForHistoryList(info);
    const isAiSearch: boolean = this.isAiSearchQuery(query);

    return { query, dm, queries, isAiSearch };
  }

  private isAiSearchQuery(query: string): boolean {
    const tree: UrlTree = this.serializer.parse(`?${query}`);
    const params = tree.queryParams;

    return !!params?.aiSearchId && String(params?.aiSearchOn) !== 'false';
  }

  private getSearchQueryFromHistoryByLog(log: AdminLogDto): SearchQueryFromHistory {
    const { query, autocompleteDto, dc } = log;

    return { query, autocompleteDto, dm: dc };
  }

  private getParseFiltersInfo(info: SearchQueryFromHistory): ParseFiltersInfo {
    const { query, autocompleteDto } = info;
    const requestInfo: SearchRequestInfo = this.getRequestInfoFromQuery(query);
    const handledAutocomplete: HandledAutocompleteResponse =
      this.autocompleteService.getHandledAutocompleteResponse(autocompleteDto);

    return {
      requestInfo,
      autocompleteResponse: handledAutocomplete,
    };
  }

  private static getQueryStringFromUrl(url: string): string {
    const queryStringFullList: string[] = url?.split('?');

    return queryStringFullList?.length > 1 ? `?${queryStringFullList[1]}` : '';
  }

  private static getFullyEncodedUrl(url: string): string {
    const encodedComma: string = encodeURIComponent(',');

    return url.replace(/,/g, encodedComma);
  }

  /**
   * Load AI search data from URL parameter and update state.
   */
  loadAiSearchDataFromUrl(): Observable<null> {
    const aiSearchId = this.requestInfo.aiSearchId;

    const aiSearchOnParam = this.requestInfo.aiSearchOn;
    if (aiSearchOnParam !== undefined && aiSearchOnParam !== null) {
      const isEnabled = String(aiSearchOnParam) !== 'false';
      this.aiSearchStateService.setEnabled(isEnabled);
    }

    const savedAiSearch = this.aiSearchStateService.getSavedAiSearch();
    const idChanged = !savedAiSearch || savedAiSearch.id !== aiSearchId;

    if (aiSearchId && idChanged) {
      return this.aiSearchService.getAiSearch(aiSearchId).pipe(
        filter((v) => !!v),
        tap((dto: AiSearchDto) => {
          this.aiSearchStateService.applyAiSearchResponse(dto);
          this.aiSearchStateService.setGlobalSearchSubmitted(true);
        }),
        map(() => null),
        catchError(() => of(null)),
      );
    }

    if (aiSearchId) {
      // The AI search is already in state and its results are on screen (e.g.
      // just forked + auto-searched from a shared search). The search WAS
      // submitted for this id — mark it so, mirroring the idChanged branch above.
      // The fork left the flag false; without restoring it the state is stuck at
      // a stale derived value and a later accuracy change can't re-show the
      // SEARCH button.
      this.aiSearchStateService.setGlobalSearchSubmitted(true);
    }

    return of(null);
  }

  /**
   * Check if navigating to search results with current requestInfo would result in the same URL.
   * Normalizes URLs by removing locale prefixes for accurate comparison.
   */
  private isNavigatingToSameUrl(): boolean {
    const targetTree: UrlTree = this.router.createUrlTree([SystemPages.SEARCH_RESULTS], {
      queryParams: this.requestInfo,
    });
    const targetUrl: string = this.serializer.serialize(targetTree);
    const currentUrl: string = this.normalizeUrlForComparison(this.router.url);
    const normalizedTargetUrl: string = this.normalizeUrlForComparison(targetUrl);

    return currentUrl === normalizedTargetUrl;
  }

  private normalizeUrlForComparison(url: string): string {
    if (!url) {
      return '';
    }

    return url.replace(/^\/(en|ua|pl)(\/|$)/, '/');
  }

  static getRequiredPriviligeForSearchRequest(
    parsedFilters: SearchFilter[],
  ): AccountPrivilegeType | null {
    let requiredPriviligeType: AccountPrivilegeType | null = null;

    const nonEmptyFilters = parsedFilters.filter((f) => f.doesHaveAnyQuery());
    if (nonEmptyFilters?.length > 0) {
      if (nonEmptyFilters.some((f) => f.isForProfessionalPlanOnly())) {
        requiredPriviligeType = 'paid';
      }

      if (nonEmptyFilters.some((f) => f.isForSuperAdminOnly())) {
        requiredPriviligeType = 'superAdmin';
      }
    }

    return requiredPriviligeType;
  }
}
